import { createRouter, createWebHistory } from 'vue-router'

import Home from '../views/Home.vue'
import Features from '../views/Features.vue'
import Plans from '../views/Plans.vue'
import Faq from '../views/Faq.vue'
import Contact from '../views/Contact.vue'
import Policy from '../views/Policy.vue'
import Privacy from '../views/Privacy.vue'
import UserProfile from '../views/UserProfile.vue'
import ResetPasswordLanding from '../views/ResetPasswordLanding.vue'
import NotFound from '../views/NotFound.vue'

import { handleEvent } from './handle-event';
import { handleHead } from './handle-head';

// Per-route SEO metadata. `title` + `description` are applied to the
// document head on every navigation by handle-head.js. Keep titles
// unique + under ~60 chars and descriptions ~150-160 chars; lead with
// the page's primary intent so each route earns its own SERP snippet
// instead of all pages sharing the homepage's.
const routes = [
  {
    path: '/',
    name: 'home',
    component: Home,
    meta: {
      title: 'Bzioup: Your last words, just in case.',
      description: 'Bzioup helps you prepare your wishes and last words for the people you love: sealed now, shared the moment it matters. Private, secure, and yours.',
    }
  },
  {
    path: '/features',
    name: 'features',
    component: Features,
    meta: {
      title: 'Features - Bzioup',
      description: 'Public and private wishes, sealed messages, trusted recipients, and more. See everything Bzioup gives you to prepare what matters, the way you want.',
    }
  },
  {
    path: '/plans',
    name: 'plans',
    component: Plans,
    meta: {
      title: 'Plans & Pricing - Bzioup',
      description: 'Start free, upgrade when you need more. Compare Bzioup\'s Free, Super, and Master plans and choose the right level of protection for your wishes.',
    }
  },
  {
    path: '/faq',
    name: 'faq',
    component: Faq,
    meta: {
      title: 'FAQ - Bzioup',
      description: 'How does Bzioup work, when are wishes shared, and how is your data protected? Answers to the most common questions about preparing your last words.',
    }
  },
  {
    path: '/contact',
    name: 'contact',
    component: Contact,
    meta: {
      title: 'Contact - Bzioup',
      description: 'Questions, feedback, or support? Get in touch with the Bzioup team.',
    }
  },
  {
    path: '/policy',
    name: 'policy',
    component: Policy,
    meta: {
      title: 'Terms of Use - Bzioup',
      description: 'The terms and conditions governing your use of Bzioup.',
    }
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: Privacy,
    meta: {
      title: 'Privacy Policy - Bzioup',
      description: 'How Bzioup collects, uses, and protects your personal data. Your privacy is the foundation of everything we build.',
    }
  },
  // Public user-profile landing. Three roles:
  // 1. Marketing pitch for non-mobile users who follow a shared
  //    profile link.
  // 2. "Get the app" CTA + App Store / Play Store buttons when the
  //    mobile app isn't installed (this page is the fallback the
  //    Universal Link / App Link points at. when the OS doesn't
  //    intercept the URL because the app isn't there, the browser
  //    lands here instead of seeing a 404).
  // 3. iOS smart-app-banner target (once an App Store ID exists).
  // Path MUST match the `/u/*` paths declared in
  //   hello/public/.well-known/apple-app-site-association
  //   hello/public/.well-known/assetlinks.json (Android handles via
  //     the bzioup-quasar AndroidManifest intent-filter)
  // bzioup-quasar/src/layouts/App.vue DEEP_LINK_PATH_PREFIXES
  // If you rename `/u/`, update those three places too.
  {
    path: '/u/:userName',
    name: 'user-profile',
    component: UserProfile,
    // Generic fallback meta. UserProfile.vue overrides document.title
    // with the actual @userName once mounted; this just keeps the
    // pre-mount title/description off the homepage default.
    meta: {
      title: 'Profile - Bzioup',
      description: 'A Bzioup profile. Get the app to connect and share what matters.',
    }
  },
  // Browser fallback for the recovery-email link, which the OS claims as
  // an App Link when Bzioup is installed. Path mirrored in aldo's
  // update-password-send-code, the AASA, AndroidManifest and quasar App.vue.
  {
    path: '/hello2/reset-password',
    name: 'reset-password-landing',
    component: ResetPasswordLanding,
    meta: {
      title: 'Reset your password - Bzioup',
      description: 'Reset your Bzioup password from the mobile app.',
      noindex: true,
    }
  },
  // nginx serves index.html for every unmatched path, so without this
  // the router matches nothing and renders a header and footer around
  // an empty page.
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFound,
    meta: {
      title: 'Page not found - Bzioup',
      description: 'This Bzioup page doesn\'t exist.',
      noindex: true,
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL), // createWebHashHistory(), // 
  routes,
  scrollBehavior(to) {
    // If the route has a hash (e.g. /features#heartbeat), smooth-scroll
    // to that element. Otherwise reset to the top of the new page.
    // Anchor handling matters for cross-page deep-links; the Home
    // heartbeat teaser links to /features#heartbeat, and without this
    // the user would land at the top of /features instead of at the
    // anchored section.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    return { top: 0 }
  }
})

handleEvent(router);
handleHead(router);

export default router
